import * as React from 'react';
import { AriaAttributes, forwardRef, useCallback } from 'react';
import { ValidIconName } from './Icon';
import {
  DefaultTooltipOptions,
  useSingleAndDoubleClick,
  useTooltipDefault
} from '../../lib';
import { ClassNameProp } from '..';
import { ButtonIcon } from './internal/ButtonIcon';

/**
 * Properties used for a {@link Tool}.
 */
export interface ToolProps
  extends ClassNameProp,
    DefaultTooltipOptions,
    Pick<AriaAttributes, 'aria-label' | 'aria-expanded'> {
  /**
   * Defines the look of the tool.
   */
  variant?: 'transparent' | 'gray' | 'white';
  /**
   * The tool icon.
   */
  icon: ValidIconName;
  /**
   * Tool click handler.
   */
  onClick: React.MouseEventHandler;
  /**
   * Optional tool text.
   */
  text?: string;
  /**
   * If the tool is disabled.
   */
  disabled?: boolean;
  /**
   * Tool tooltip.
   */
  tooltip?: string;
}

/**
 * A simple tool.
 *
 * @param properties properties for the tool. At least one of {text, tooltip} attributes is required
 * @constructor
 * @return the created {@link JSX.Element} for the tool
 */
export const Tool = forwardRef(function Tool(
  {
    variant,
    onClick,
    ...props
  }: ToolProps &
    (Required<Pick<ToolProps, 'tooltip'>> | Required<Pick<ToolProps, 'text'>>),
  ref
): JSX.Element {
  // create tooltip
  const [tooltipElement, triggerProps] = useTooltipDefault(
    props.tooltip,
    props.tooltipOptions
  );

  // create click handlers
  // click handler which automatically hides the tooltip
  const onClickHidingTooltip = useCallback(
    e => {
      triggerProps.onMouseLeave();
      onClick(e);
    },
    [onClick, triggerProps]
  );
  // passing no dblClickHandler will return a handler that simply stops propagation
  const [clickHandler, dblClickHandler] = useSingleAndDoubleClick(
    onClickHidingTooltip
  );

  const classes = ['ehs-tool'];
  if (!variant) {
    variant = 'transparent';
  }
  classes.push(variant);
  if (props.disabled) classes.push('disabled');
  if (props.className) classes.push(props.className);
  return (
    <>
      {props.tooltip ? tooltipElement : <></>}
      <button
        {...(props.tooltip && triggerProps)}
        disabled={props.disabled}
        className={classes.join(' ')}
        onClick={props.disabled ? undefined : clickHandler}
        onDoubleClick={dblClickHandler}
        aria-label={
          props['aria-label'] ?? (props.text || props.tooltip || undefined)
        }
        type="button"
        aria-expanded={props['aria-expanded']}
      >
        <ButtonIcon iconName={props.icon} />
        {props.text ? <span className={'tool-text'}>{props.text}</span> : <></>}
      </button>
    </>
  );
});
